/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import PageTitle from '../../../../client/js/components/PageTitle'
import useQueryParams from '../../../../client/js/hooks/useQueryParams'
import {
  downloadManagementReport,
  fetchManagementReportData,
  fetchManagementReportYearsAvailable
} from '../../lib/rest/managementReportAPIClient'
import CustomerInput from '../inputs/CustomerInput'
import EstateInput from '../inputs/EstateInput'
import ManagementReport from './ManagementReport'
import { useQuery } from '@tanstack/react-query'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { parseNumber } from '@jalik/form-parser'
import { Box, Button, Card, Container, Divider, Group, Select, Stack } from '@mantine/core'
import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { handleValueChange } from '../../../../client/js/lib/form'
import Toolbar from '../../../../client/js/components/Toolbar'
import RequirePerm from '../../../auth/components/RequirePerm'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { getTransactionCreateLink } from '../../lib/links/transactionLinks'
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons/faMinusCircle'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import Page from '../../../../client/js/components/Page'
import BreadcrumbCard from '../../../../client/js/components/breadcrumb/BreadcrumbCard'
import AdminBreadcrumb from '../../../../client/js/components/breadcrumb/AdminBreadcrumb'
import { getManagementReportListLink } from '../../lib/links/managementReportLinks'

function ManagementReportPage () {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useQueryParams()

  const customerId = useParsedQueryParam('customerId', parseNumber)
  const estateId = useParsedQueryParam('estateId', parseNumber)
  const year = useParsedQueryParam('year', parseNumber)

  const customerFilter = useMemo(() => ({
    isLessor: true
  }), [])

  const estateFilter = useMemo(() => ({
    customerId
  }), [customerId])

  const isReady = customerId != null && estateId != null && year != null

  const reportQuery = useQuery({
    enabled: customerId != null && estateId != null && year != null,
    queryKey: ['managementReport', customerId, estateId, year],
    queryFn: () =>
      fetchManagementReportData({
        customerId,
        estateId,
        year,
        isManagementReport: true
      }),
    refetchOnWindowFocus: false
  })

  const reportFileQuery = useQuery({
    enabled: false,
    queryKey: ['downloadManagementReport', customerId, estateId, year],
    queryFn: () => downloadManagementReport({ customerId, estateId, year })
  })

  const yearsQuery = useQuery({
    enabled: customerId != null && estateId != null,
    queryKey: ['leaseRentalReportYears', customerId, estateId],
    queryFn: () =>
      fetchManagementReportYearsAvailable({
        customerId,
        estateId
      }),
    refetchOnWindowFocus: false
  })
  const years = useMemo<number[]>(
    () => (yearsQuery?.data?.data || []),
    [yearsQuery?.data?.data]
  )

  const setFilter = useCallback((name: string, value: string) => {
    const next = { ...searchParams, [name]: value }

    if (name === 'customerId') {
      delete next.year
      delete next.estateId
    } else if (name === 'estateId') {
      delete next.year
    }
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const handleYearChange = useCallback((value?: string) => {
    const dt = DateTime.fromObject({ year: Number(value) })

    if (value != null && dt.isValid) {
      setSearchParams({ ...searchParams, year: value })
    }
  }, [searchParams, setSearchParams])

  const transactionParams = useMemo(() => ({
    customerId,
    estateId
  }), [customerId, estateId])

  const title = [
    intl.formatMessage({
      id: 'management_report',
      defaultMessage: 'Rapport de gérance'
    }),
    year
  ].join(' ')

  return (
    <Page>
      <form method="get">
        <Container size="xl">
          <PageTitle
            hidden
            title={title}
          />

          <BreadcrumbCard>
            <AdminBreadcrumb>
              <AnchorLink to={getManagementReportListLink()}>
                <FormattedMessage
                  id="management_reports"
                  defaultMessage="Rapports de gérance"
                />
              </AnchorLink>
              <span>{title}</span>
            </AdminBreadcrumb>
          </BreadcrumbCard>

          <Card shadow="sm">
            <Stack>
              <Toolbar align="end">
                <CustomerInput
                  // autoFocus={!customerId}
                  queryFilter={customerFilter}
                  label={intl.formatMessage({
                    id: 'owner',
                    defaultMessage: 'Propriétaire'
                  })}
                  name="customerId"
                  style={{ flexGrow: 1 }}
                  onChange={handleValueChange('customerId', setFilter)}
                  required
                  value={customerId != null ? String(customerId) : null}
                />
                <EstateInput
                  // autoFocus={customerId && !estateId}
                  disabled={!customerId}
                  queryFilter={estateFilter}
                  name="estateId"
                  style={{ flexGrow: 1 }}
                  onChange={handleValueChange('estateId', setFilter)}
                  required
                  value={estateId != null ? String(estateId) : null}
                />
                <Select
                  disabled={!customerId || yearsQuery.isFetching}
                  id="year-field"
                  label={intl.formatMessage({
                    id: 'year',
                    defaultMessage: 'Année'
                  })}
                  name="year"
                  onChange={handleYearChange}
                  required
                  value={year != null ? String(year) : ''}
                  data={[
                    ...years.map((value) => ({
                      value: String(value),
                      label: String(value)
                    }))
                  ]}
                />
                <Button
                  disabled={!isReady || reportQuery.isFetching || !reportQuery.isFetched}
                  loading={reportQuery.isFetching}
                  onClick={() => {
                    reportQuery.refetch()
                  }}
                  title={intl.formatMessage({
                    id: 'refresh',
                    defaultMessage: 'Rafraîchir'
                  })}
                >
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faRefresh}
                  />
                </Button>
                <Button
                  disabled={!isReady || reportFileQuery.isFetching}
                  loading={reportFileQuery.isFetching}
                  onClick={() => {
                    reportFileQuery.refetch()
                  }}
                  title={intl.formatMessage({
                    id: 'download',
                    defaultMessage: 'Télécharger'
                  })}
                >
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faDownload}
                  />
                </Button>

                <RequirePerm
                  permissions={[
                    'write:invoice',
                    'write:transaction'
                  ]}
                  render={() => (
                    <Group gap="xs" ms="auto">
                      <Button
                        disabled={!isReady}
                        size="sm"
                        component={AnchorLink}
                        to={`${getTransactionCreateLink(true, transactionParams)}`}
                      >
                        <FontAwesomeIcon icon={faMinusCircle} />
                        <Box visibleFrom="lg" ml="xs">
                          <FormattedMessage
                            id="expense"
                            defaultMessage="Dépense"
                          />
                        </Box>
                      </Button>
                      <Button
                        disabled={!isReady}
                        size="sm"
                        component={AnchorLink}
                        to={`${getTransactionCreateLink(false, transactionParams)}`}
                      >
                        <FontAwesomeIcon icon={faPlusCircle} />
                        <Box visibleFrom="lg" ml="xs">
                          <FormattedMessage
                            id="revenue"
                            defaultMessage="Revenu"
                          />
                        </Box>
                      </Button>
                    </Group>
                  )}
                />
              </Toolbar>

              <Divider />

              <LoadingHelper
                query={reportQuery}
                render={() => <ManagementReport
                  data={reportQuery.data}
                  isManagementReport
                  onRefresh={reportQuery.refetch}
                />}
              />
            </Stack>
          </Card>
        </Container>
      </form>
    </Page>
  )
}

export default ManagementReportPage
